import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { Subscription, Observable } from 'rxjs';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { KeycloakAuthService } from '../services/keycloak-auth.service';
import { User } from '../models/userModel';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';

type LoginMode = 'login' | 'forgot-email' | 'reset-password';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatInputModule,
    MatFormFieldModule
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  private authService = inject(KeycloakAuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private userSubscription: Subscription | null = null;

  currentUser$: Observable<User | null>;
  minLength = 8;
  maxLength = 18;
  isLoading = false;
  mode: LoginMode = 'login'; // State management for different views
  errorMessage: string | null = null;
  successMessage: string | null = null;
  errorDetails: string | null = null;  // For subtle error details

  // Dynamic forms based on mode
  loginForm: FormGroup;
  forgotEmailForm: FormGroup;
  resetForm: FormGroup;
  resetToken: string | null = null;  

  constructor() {
    this.currentUser$ = this.authService.currentUser$;
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, 
                      Validators.minLength(this.minLength), 
                      Validators.maxLength(this.maxLength)]]
    });
    this.forgotEmailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
    this.resetForm = this.fb.group({
      resetToken: ['', [Validators.required]],
      newPassword: ['', [Validators.required, Validators.minLength(this.minLength)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(this.minLength)]]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    // Subscribe to currentUser$ to check if user is already logged in
    this.userSubscription = this.currentUser$.subscribe(user => {
      if (user) {
        this.isLoading = false;
        this.router.navigate(['/home']);
      }
    });

    // Optional: Check query params for token to auto-enter reset mode (if coming from email link)
    this.route.queryParams.subscribe(params => {
      const token = params['token'];
      if (token && this.mode !== 'reset-password') {
        this.mode = 'reset-password';
        this.resetToken = token;
        this.resetForm.patchValue({ resetToken: token });
        this.successMessage = 'Token loaded from link. Enter your new password below.';
      }
    });
  }

  ngOnDestroy(): void {
    // Clean up subscription
    this.userSubscription?.unsubscribe();
  }

  // Custom validator for password match
  passwordMatchValidator: (form: AbstractControl) => ValidationErrors | null = (form: AbstractControl): ValidationErrors | null => {
    const newPassword = form.get('newPassword')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;
    return newPassword === confirmPassword ? null : { mismatch: true };
  };

  /**
   * Initiates the Google Login flow via AuthService
   */
  loginWithGoogle(): void {
    this.isLoading = true;
    this.authService.login();
  }

  /**
   * Logs out the current user via AuthService
   */
  logoutCurrentUser(): void {
    this.authService.logout();
  }

  // Mode: 'login'
  loginWithEmail(): void {
    if (this.loginForm.valid) {
      this.isLoading = true;
      this.errorMessage = null;
      this.errorDetails = null;
      const { email, password } = this.loginForm.value;
      this.authService.loginDigifyPassword(email, password)
        .then((response) => {
          console.log('response', response);
          localStorage.setItem('session_id', response.sessionId);
          this.authService.fetchUser(); // Fetch and store user profile
          this.router.navigate(['/home']);
        })
        .catch((err) => {
          // Improved error handling: Prioritize Keycloak-specific details
          if (err.error?.details) {
            // Backend sends 'details' from Keycloak (e.g., "Account is not fully set up")
            this.errorMessage = err.error.details;
          } else if (err.error?.error) {
            this.errorMessage = err.error.error;
          } else {
            this.errorMessage = 'Login failed';
          }
          this.errorDetails = err.error?.details ? null : err.message || null; // Only show details if not already used in message
          console.error('Login error: ', err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    } else {
      this.errorMessage = 'Please fill in all fields correctly';
    }
  }

  // Switch to forgot-email mode
  onForgotPassword(): void {
    this.mode = 'forgot-email';
    this.errorMessage = null;
    this.errorDetails = null;
    this.successMessage = null;
    this.resetToken = null;  // Clear any prior token
  }

  // Mode: 'forgot-email'
  sendResetEmail(): void {
    if (this.forgotEmailForm.valid) {
      this.isLoading = true;
      this.errorMessage = null;
      this.errorDetails = null;
      const { email } = this.forgotEmailForm.value;
      this.authService.forgotPassword(email)
        .then((response) => {
          console.log('Forgot success:', response);  // Log full response
          this.successMessage = response.message;
          this.resetToken = response.resetToken;  // Store token
          this.resetForm.patchValue({ 
            resetToken: response.resetToken,  // Auto-fill hidden field
            newPassword: '',
            confirmPassword: ''
          });
          this.mode = 'reset-password'; // Switch modes
        })
        .catch((err) => {
          console.error('Forgot password error: ', err);
          let mainError = 'Failed to verify email';
          if (err.error?.error) {
            mainError = err.error.error;  // e.g., "User with this email does not exist"
          } else if (err.message) {
            mainError = err.message;
          }
          this.errorMessage = mainError;
          this.errorDetails = err.error?.details || null;
        })
        .finally(() => {
          this.isLoading = false;
        });
    } else {
      this.errorMessage = 'Please enter a valid email';
    }
  }

  // Mode: 'reset-password'
  setNewPassword(): void {
    if (this.resetForm.valid) {
      this.isLoading = true;
      this.errorMessage = null;
      this.errorDetails = null;
      let token = this.resetForm.get('resetToken')?.value || this.resetToken;  // Form or stored fallback
      const { newPassword } = this.resetForm.value;
      if (!token) {
        this.errorMessage = 'Reset token missing - please try again.';
        this.isLoading = false;
        return;
      }
      this.authService.resetPassword(token, newPassword)
        .then((response) => {
          console.log('Reset success:', response);
          this.successMessage = response.message;
          this.resetToken = null;  // Clear stored token
          // Auto-switch back to login after delay
          setTimeout(() => {
            this.mode = 'login';
            this.resetForm.reset();
          }, 3000);
        })
        .catch((err) => {
          console.error('Reset password error: ', err);
          let mainError = 'Failed to reset password';
          if (err.error?.error) {
            mainError = err.error.error;  // e.g., "Invalid or expired reset token"
          } else if (err.message) {
            mainError = err.message;
          }
          this.errorMessage = mainError;
          this.errorDetails = err.error?.details || null;
        })
        .finally(() => {
          this.isLoading = false;
        });
    } else {
      this.errorMessage = 'Please fill in all fields correctly. New passwords must match.';
    }
  }

  // Go back to login mode
  goBackToLogin(): void {
    this.mode = 'login';
    this.errorMessage = null;
    this.errorDetails = null;
    this.successMessage = null;
    this.resetToken = null;  // Clear token
    this.forgotEmailForm.reset();
    this.resetForm.reset();
  }

  /**
   * Helper function to get initials from user name
   */
  getInitials(user: User | null): string {
    if (!user) return '??';
    const first = user.firstName ? user.firstName[0] : '';
    const last = user.lastName ? user.lastName[0] : '';
    if (first && last) {
      return `${first}${last}`.toUpperCase();
    } else if (user.username) {
      return user.username.substring(0, Math.min(user.username.length, 2)).toUpperCase();
    }
    return '??';
  }

  // Getter for current heading
  get currentHeading(): string {
    switch (this.mode) {
      case 'forgot-email': return 'Verify Email';
      case 'reset-password': return 'Set New Password';
      default: return 'Login';
    }
  }

  // Getter for current subheading
  get currentSubheading(): string {
    switch (this.mode) {
      case 'forgot-email': return 'Enter your email to verify and reset your password';
      case 'reset-password': return 'Enter your new password below';
      default: return 'Sign in to access your workspace';
    }
  }

  // Getter for showGoogleSignIn
  get showGoogleSignIn(): boolean {
    return this.mode === 'login';
  }

  // Getter for showBackButton
  get showBackButton(): boolean {
    return this.mode !== 'login';
  }
}